import React, { useRef, useCallback, useContext, useLayoutEffect } from 'react'
import KeyHandler, { KEYUP } from 'react-key-handler'
import ModalHeader from './ModalHeader'
import ModalBody from './ModalBody'
import styled, { css } from 'styled-components'
import DisableBodyScroll from 'components/DisableBodyScroll'
import { useFocusTrap } from './Modal.hooks'
import ModalContext from 'containers/ModalManager/ModalContext'
import { breakpoint } from 'utils'
import { debounce } from 'debounce'

const BackdropStyle = styled.div`
  position: fixed;
  z-index: 1000;

  top: 0;
  left: 0;

  padding: 80px 0 0; /* stylelint-disable-line scales/space */

  ${breakpoint('xs')`
    padding: 5vh 0; /* stylelint-disable-line scales/space */
  `}

  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: flex-start;

  background-color: ${({ theme }) => theme.colors.background.tertiary[3]};

  user-select: none;

  &.modal-enter {
    opacity: 0;
  }
  &.modal-enter-active {
    opacity: 1;
    transition: opacity ${({ theme }) => theme.transitions.duration[2]};
  }
  &.modal-exit {
    opacity: 1;
  }
  &.modal-exit-active {
    opacity: 0;
    transition: opacity ${({ theme }) => theme.transitions.duration[2]};
  }
`

const Container = styled.section(
  ({ theme }) => css`
    margin: auto 0 0;

    width: 100%;
    max-width: 512px;
    font-size: 0.8125rem;

    max-height: 100%;
    transform: translate3d(0, 0, 0);

    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: none;

    border-radius: ${theme.radii.xxxlarge} ${theme.radii.xxxlarge} 0 0;
    background-color: ${theme.colors.background.light.base};

    ${breakpoint('xs')`
      border-radius: ${theme.radii.xxxlarge};
      margin: auto 0;
    `}

    .modal-enter & {
      opacity: 0;
      transform: translateY(10px);
    }

    .modal-enter-active & {
      opacity: 1;
      transform: translateY(0);
      transition: ${`
        opacity  ${theme.transitions.duration[1]},
        transform  ${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}
    `};
    }

    .modal-exit & {
      opacity: 1;
      transform: translateY(0);
    }

    .modal-exit-active & {
      opacity: 0;
      transform: translateY(10px);
      transition: ${`
      opacity  ${theme.transitions.duration[2]},
      transform  ${theme.transitions.duration[1]} ${theme.transitions.timing.easeInOut}
    `};
    }

    & p {
      margin: 0;
      padding-bottom: 20px;
    }

    & p:last-child {
      padding-bottom: 0;
    }

    /* Ensure modals without a header have some spacing above the body */
    & ${ModalBody} {
      &:first-child {
        margin-top: 20px;
      }
    }

    /* stylelint-disable selector-type-no-unknown */
    ${ModalBody},
    ${ModalBody} + ${ModalBody} {
      border-bottom: 1px solid ${theme.colors.border.dark.base};
      padding-top: 20px;

      &:first-of-type {
        padding-top: 0;
      }

      &:last-of-type {
        border-bottom: 0;
      }
    }

    /* stylelint-enable */
    user-select: text;
  `
)

interface BackdropProps {
  onClick?: () => void
}

const Backdrop: React.FC<BackdropProps> = ({ children, onClick }) => {
  const backdropRef = useRef<HTMLDivElement>(null)
  const hasMouseDown = useRef(false)
  useFocusTrap(backdropRef)

  /**
   * Unintended modal closing is happening because of event
   * bubbling to Backdrop, so we need to detect when a complete
   * click (mouse down and up) is happening within Backdrop.
   * The modal will only be closed when a previous mouseDown
   * event is triggered before click.
   *
   * Learn more: https://github.com/sketch-hq/cloud-frontend/pull/2183
   */
  const handleMouseDown = useCallback((event: any) => {
    if (event.target !== backdropRef.current) {
      return
    }

    hasMouseDown.current = true
  }, [])

  const handleClick = useCallback(
    (event: any) => {
      if (
        !onClick ||
        event.target !== backdropRef.current ||
        !hasMouseDown.current
      ) {
        return
      }

      onClick()
    },
    [onClick]
  )

  return (
    <BackdropStyle
      data-testid="modal-backdrop"
      ref={backdropRef}
      {...(onClick && { onClick: handleClick })}
      onMouseDown={handleMouseDown}>
      {children}
    </BackdropStyle>
  )
}

export interface ModalProps {
  title?: React.ReactNode
  onCancel: () => void
  style?: React.CSSProperties
  onUpdateScroll?: (hasOverflow: boolean) => void
}

/**
 * Set of Modal primitives to build modals/dialogs. These primitives work
 * together with `ModalProvider` and `ModalRoot`, that handles the logic to
 * open and close modals.
 *
 * Most basic example:
 *
 * ```jsx
 * <Modal title="Modal tile">
 *  <Modal.Body>Body text</Modal.Body>
 *  <Modal.Footer>Footer</Modal.Footer>
 * </Modal>
 * ```
 *
 * Some considerations:
 *
 * - To open/hide modals you should use the hook `useModalContext`.
 * - You'll get some useful injected props in any modal: `isModalOpen` and `hideModal`.
 */
const Modal: React.FC<ModalProps> = ({
  title,
  onCancel,
  style,
  children,
  onUpdateScroll,
  ...otherProps
}) => {
  const { cancelOnClickOutside } = useContext(ModalContext)
  const containerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    // used to notify the parent component if the modal has overflow
    const updateBodyHeight = () => {
      const clientHeight = containerRef?.current?.clientHeight ?? 0
      const scrollHeight = containerRef?.current?.scrollHeight ?? 0

      onUpdateScroll && onUpdateScroll(clientHeight < scrollHeight)
    }

    updateBodyHeight()

    window.addEventListener('resize', () => debounce(updateBodyHeight, 250))

    return () => window.removeEventListener('resize', updateBodyHeight)
  }, [containerRef, onUpdateScroll])

  return (
    <>
      {cancelOnClickOutside && (
        <KeyHandler keyEventName={KEYUP} keyCode={27} onKeyHandle={onCancel} />
      )}
      <DisableBodyScroll />
      <Backdrop {...(cancelOnClickOutside && { onClick: onCancel })}>
        <Container
          role="dialog"
          style={style}
          {...otherProps}
          ref={containerRef}>
          {title && <ModalHeader>{title}</ModalHeader>}
          {children}
        </Container>
      </Backdrop>
    </>
  )
}

export default Modal
